import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  buildCreateAmountsPayload,
  createInitialAmountsValue,
  GivingLinkAmountsField,
  isAmountsValueValid,
  type GivingLinkAmountsValue,
} from '@/components/payments/giving-link-amounts-field';
import { toGivingLinkFundOptions } from '@/components/payments/giving-link-fund-options';
import { GivingLinkImageField } from '@/components/payments/giving-link-image-field';
import { HubtelAccountField } from '@/components/payments/hubtel-account-field';
import { fundTypeLabel } from '@/components/shared/fund-type-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { ONLINE_GIVING_SETTINGS_PATH } from '@/lib/online-giving-settings';
import { usePaymentConnection } from '@/lib/payment-connection';
import {
  PERMISSIONS,
  hasPermission,
  isAdminOrOwner,
  useCurrentMembership,
} from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

interface Fund {
  id: string;
  name: string;
  fundType: string;
  customTypeName?: string | null;
  defaultCurrency: string;
  isActive: boolean;
}

interface GivingLink {
  id: string;
  slug: string;
  title: string;
}

export const CreateGivingLinkDialog = ({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (link: GivingLink) => void;
}) => {
  const { t } = useTranslation('payments');
  const { t: tGiving } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useCurrentMembership();
  const canConfigurePayments =
    isAdminOrOwner(membership) || hasPermission(membership, PERMISSIONS.PAYMENTS_CONFIGURE);
  const { isReady: paymentReady, isLoading: paymentLoading } = usePaymentConnection(open);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [fundIds, setFundIds] = useState<string[]>([]);
  const [defaultFundId, setDefaultFundId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<GivingLinkAmountsValue>(createInitialAmountsValue);

  const funds = useQuery({
    queryKey: ['giving', 'funds', { activeOnly: true }],
    queryFn: () =>
      api.get<{ data: Fund[] }>('/giving/funds', {
        query: { page: 1, pageSize: 100, activeOnly: 'true' },
      }),
    enabled: open && paymentReady,
  });

  const fundOptions = useMemo(
    () =>
      toGivingLinkFundOptions(
        (funds.data?.data ?? []).filter((fund) => fund.fundType !== 'tithe'),
        {
          description: (fund) =>
            tGiving('form.fundOptionDescription', {
              type: fundTypeLabel(fund, tGiving),
              currency: fund.defaultCurrency,
            }),
        },
      ),
    [funds.data?.data, tGiving],
  );

  const amountCurrency =
    (funds.data?.data ?? []).find((fund) => fund.id === defaultFundId)?.defaultCurrency ??
    church?.baseCurrency;

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setImageKey(null);
    setImagePreviewUrl(null);
    setFundIds([]);
    setDefaultFundId(null);
    setAccountId(null);
    setAmounts(createInitialAmountsValue());
  }, [open]);

  useEffect(() => {
    if (defaultFundId && !fundIds.includes(defaultFundId)) {
      setDefaultFundId(fundIds[0] ?? null);
    }
    if (!defaultFundId && fundIds[0]) {
      setDefaultFundId(fundIds[0]);
    }
  }, [defaultFundId, fundIds]);

  const create = useMutation({
    mutationFn: () =>
      api.post<GivingLink>('/payments/giving-links', {
        title: title.trim(),
        description: description.trim() || null,
        imageKey,
        allowedFundIds: fundIds,
        defaultFundId,
        financialPositionItemId: accountId,
        ...buildCreateAmountsPayload(amounts),
      }),
    onSuccess: (link) => {
      toast.success(t('links.created'));
      void queryClient.invalidateQueries({ queryKey: ['payments', 'giving-links'] });
      onOpenChange(false);
      onCreated?.(link);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const canSubmit =
    paymentReady &&
    title.trim().length > 0 &&
    fundIds.length > 0 &&
    Boolean(defaultFundId) &&
    Boolean(accountId) &&
    isAmountsValueValid(amounts) &&
    !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('links.create')}</DialogTitle>
        </DialogHeader>
        {paymentLoading ? (
          <p className="py-8 text-center text-body-sm text-fg-secondary" role="status">
            {tCommon('state.loading')}
          </p>
        ) : !paymentReady ? (
          <div className="space-y-4 py-2">
            <p className="text-body-sm text-fg-secondary">
              {canConfigurePayments
                ? t('links.requiresConnectionDescription')
                : t('links.requiresConnectionAdmin')}
            </p>
            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {tCommon('action.close')}
              </Button>
              {canConfigurePayments ? (
                <Button asChild>
                  <Link to={ONLINE_GIVING_SETTINGS_PATH}>{t('links.requiresConnectionAction')}</Link>
                </Button>
              ) : null}
            </DialogFooter>
          </div>
        ) : (
          <>
        <ScrollArea className="max-h-[70vh] pr-2">
          <div className="space-y-6">
            <div>
              <Label htmlFor="giving-link-title">{t('links.form.title')}</Label>
              <Input
                id="giving-link-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="giving-link-description">{t('links.form.description')}</Label>
              <Textarea
                id="giving-link-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
              />
            </div>
            <GivingLinkImageField
              idPrefix="create-giving-link"
              imageKey={imageKey}
              previewUrl={imagePreviewUrl}
              onChange={({ imageKey: nextKey, previewUrl }) => {
                setImageKey(nextKey);
                setImagePreviewUrl(previewUrl);
              }}
            />
            <div>
              <Label>{t('links.form.funds')}</Label>
              <MultiSelectListbox
                value={fundIds}
                onChange={setFundIds}
                options={fundOptions}
                placeholder={t('links.form.fundsPlaceholder')}
                emptyMessage={t('links.form.fundsEmpty')}
                popoverPortal="dialog"
              />
            </div>
            <div>
              <Label>{t('links.form.defaultFund')}</Label>
              <Listbox
                value={defaultFundId}
                onChange={setDefaultFundId}
                options={fundOptions.filter((option) => fundIds.includes(option.value))}
                placeholder={t('links.form.defaultFundPlaceholder')}
                popoverPortal="dialog"
                disabled={fundIds.length === 0}
              />
            </div>
            <HubtelAccountField
              open={open}
              currency={amountCurrency}
              value={accountId}
              onChange={setAccountId}
            />
            <GivingLinkAmountsField
              idPrefix="create-giving-link"
              value={amounts}
              onChange={setAmounts}
              currency={amountCurrency}
            />
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate()}>
            {t('links.create')}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
